import { MapPin, RefreshCw } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import patrolScheduleCache from '../../services/patrolScheduleCache';
import { typography } from '../../styles/typography';
import leaveApi from '../LeaveManagement/leaveApi';
import { qrApi } from '../QR/QRApi';
import { IconTextButton, SearchBar } from '../ui';
import CreatePatrollingSchedule from './CreatePatrollingSchedule';
import PatrolScheduleCard from './PatrolScheduleCard';
import PatroleDetails from './PatroleDetails';
import { patrollingApi } from './PatrollingApi';
import PatrollingAssignConvo from './PatrollingAssignConvo';

const ScheduleAPatrole = () => {
  // Log screen view
  useScreenLogging('ScheduleAPatrole', {
    component: 'ScheduleAPatrole',
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [patrolSchedules, setPatrolSchedules] = useState([]);
  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPatrol, setSelectedPatrol] = useState(null);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    has_more: false,
    last_key: null,
    count: 0,
  });
  const [filters, setFilters] = useState({
    status: null, // 'scheduled', 'in_progress', 'completed', 'missed', 'cancelled'
    date: null, // ISO8601 date string (YYYY-MM-DD)
    user_id: null, // Filter by user_id
  });
  const [qrCache, setQrCache] = useState(new Map()); // Cache QR details by qr_id
  
  // Check URL params to determine if we should show details or create patrol
  const viewParam = searchParams.get('view');
  const patrolIdParam = searchParams.get('patrolId');
  const showDetails = viewParam === 'patrol-details' && patrolIdParam;
  const showCreatePatrol = viewParam === 'create-patrol';
  const showAssignConvoy = viewParam === 'assign-patrol-convoy';

  // Load selected patrol when patrolId is in URL
  useEffect(() => {
    if (showDetails && patrolIdParam && patrolSchedules.length > 0) {
      const patrol = patrolSchedules.find(p => 
        p.patrol_id === patrolIdParam || 
        p.id === patrolIdParam ||
        (p.patrol_id || p.id)?.toString() === patrolIdParam
      );
      if (patrol) {
        setSelectedPatrol(patrol);
      } else if (!isLoading) {
        // If patrol not found and not loading, go back to list
        setSearchParams({});
      }
    }
  }, [showDetails, patrolIdParam, patrolSchedules, isLoading, setSearchParams]);

  // Fetch and cache QR codes with pagination support
  const fetchQRCodes = useCallback(async () => {
    // Check if cache is already populated
    if (qrCache.size > 0) {
      return qrCache; // Return existing cache
    }

    try {
      console.log('🔄 Fetching QR codes for lookup...');
      const qrMap = new Map();
      let hasMore = true;
      let lastKey = null;
      let totalFetched = 0;

      // Fetch all pages of QR codes
      while (hasMore) {
        const options = lastKey ? { last_key: lastKey, limit: 100 } : { limit: 100 };
        const response = await qrApi.getAll(options);
        
        // Process QR codes from this page
        if (response.items && Array.isArray(response.items)) {
          response.items.forEach(qr => {
            if (qr.qr_id) {
              qrMap.set(qr.qr_id, {
                name: qr.name || 'Unnamed QR',
                address: qr.landmark || qr.address || 'No address',
                qr_id: qr.qr_id,
              });
            }
          });
          totalFetched += response.items.length;
        }
        
        // Check if there are more pages
        hasMore = response.has_more || false;
        lastKey = response.last_key || null;
        
        console.log(`📦 Fetched ${response.items?.length || 0} QR codes (Total: ${totalFetched})`);
      }
      
      setQrCache(qrMap);
      console.log(`✅ Cached ${qrMap.size} QR codes (fetched ${totalFetched} items)`);
      return qrMap; // Return the new cache
    } catch (error) {
      console.error('Error fetching QR codes:', error);
      // Return empty map - will show IDs instead
      return new Map();
    }
  }, [qrCache]);

  // Fetch patrol schedules on component mount
  const fetchPatrolSchedules = useCallback(async (forceRefresh = false, loadMore = false) => {
    setIsLoading(true);
    setError(null);
    try {
      // Check cache first if not forcing refresh and not loading more
      if (!forceRefresh && !loadMore) {
        const cachedData = patrolScheduleCache.get();
        if (cachedData) {
          console.log('📦 Using cached patrol schedules');
          setPatrolSchedules(cachedData);
          setFilteredSchedules(cachedData);
          setIsLoading(false);
          return;
        }
      } else if (forceRefresh) {
        // Clear cache on force refresh
        patrolScheduleCache.clear();
      }

      // Build API options with filters
      const apiOptions = {
        limit: 100, // Get up to 100 patrols per page
      };

      // Add filters if provided
      if (filters.status) {
        apiOptions.status = filters.status;
      }
      if (filters.date) {
        apiOptions.date = filters.date;
      }
      if (filters.user_id) {
        apiOptions.user_id = filters.user_id;
      }

      // Add pagination token if loading more
      if (loadMore && pagination.last_key) {
        apiOptions.last_key = pagination.last_key;
      }

      // Fetch patrol schedules from API
      console.log('🔄 Fetching patrol schedules from API...', apiOptions);
      const response = await patrollingApi.getAll(apiOptions);
      
      const schedules = response.patrols || [];
      console.log(`✅ Fetched ${schedules.length} patrol schedules`);
      
      // Log API event for viewing patrol schedules
      loggingService.logInfo(
        'patrol_schedule_view',
        `User viewed patrol schedules (${schedules.length} schedules)`,
        {
          endpoint: '/patrol/schedules',
          method: 'GET',
          scheduleCount: schedules.length,
          filters: {
            status: filters.status || null,
            date: filters.date || null,
            userId: filters.user_id || null,
          },
          forceRefresh: forceRefresh,
          loadMore: loadMore,
        }
      ).catch(logError => {
        console.warn('[ScheduleAPatrole] Failed to log event:', logError);
      });
      
      // Update pagination state
      setPagination({
        has_more: response.has_more || false,
        last_key: response.last_key || null,
        count: response.count || schedules.length,
      });
      
      // Ensure QR cache is loaded and get the current cache
      const currentQrCache = await fetchQRCodes();
      
      // Enrich schedules with QR details
      const enrichedSchedules = await Promise.all(
        schedules.map(async (schedule) => {
          try {
            // Get start QR details
            const startQrId = schedule.start_qr_id;
            const startQr = startQrId ? currentQrCache.get(startQrId) : null;
            const startQrName = startQr?.name || startQrId || 'Unknown';
            const startQrAddress = startQr?.address || 'No address';
            
            // Get end QR details
            const endQrId = schedule.end_qr_id;
            const endQr = endQrId ? currentQrCache.get(endQrId) : null;
            const endQrName = endQr?.name || endQrId || 'Unknown';
            const endQrAddress = endQr?.address || 'No address';
            
            // Fetch supervisor details if available using leaveApi.getUserInfo
            // Extract user_id from activity_done_by or patrol_details
            let supervisorName = 'Unknown';
            const supervisorId = schedule.activity_done_by?.split('/')?.[1] || 
                                schedule.supervisor_id ||
                                schedule.patrol_details?.[0]?.user_ids?.[0];
            
            if (supervisorId) {
              try {
                const supervisorDetails = await leaveApi.getUserInfo(supervisorId);
                supervisorName = supervisorDetails?.user_name || supervisorDetails?.name || 'Unknown';
              } catch (error) {
                console.error('Error fetching supervisor details:', error);
              }
            }
            
            return {
              ...schedule,
              startQrName,
              startQrAddress,
              startQrId,
              endQrName,
              endQrAddress,
              endQrId,
              supervisorName,
              // Ensure we have an id field for compatibility
              id: schedule.patrol_id || schedule.id,
            };
          } catch (error) {
            console.error('Error enriching schedule:', error);
            return {
              ...schedule,
              startQrName: schedule.start_qr_id || 'Unknown',
              startQrAddress: 'No address',
              startQrId: schedule.start_qr_id,
              endQrName: schedule.end_qr_id || 'Unknown',
              endQrAddress: 'No address',
              endQrId: schedule.end_qr_id,
              supervisorName: 'Unknown',
              id: schedule.patrol_id || schedule.id,
            };
          }
        })
      );
      
      // If loading more, append to existing schedules; otherwise replace
      if (loadMore) {
        setPatrolSchedules(prev => [...prev, ...enrichedSchedules]);
        setFilteredSchedules(prev => [...prev, ...enrichedSchedules]);
      } else {
        // Cache the enriched schedules only on initial load or refresh
        if (!loadMore) {
          patrolScheduleCache.set(enrichedSchedules);
        }
        setPatrolSchedules(enrichedSchedules);
        setFilteredSchedules(enrichedSchedules);
      }
    } catch (error) {
      console.error('Error fetching patrol schedules:', error);
      setError(error.message || 'Failed to fetch patrol schedules. Please try again.');
      // Show user-friendly error message
      if (error.message) {
        console.error('API Error:', error.message);
      }
      // Only clear schedules if not loading more (to preserve existing data)
      if (!loadMore) {
        setPatrolSchedules([]);
        setFilteredSchedules([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPatrolSchedules();
  }, [fetchPatrolSchedules]);

  // Clear cache when returning from create-patrol views
  // (a new patrol might have been created)
  useEffect(() => {
    // If we're on the main list view and we just came from create views
    // we should refresh to get the latest data
    if (!showCreatePatrol && !showDetails) {
      // Check if we should invalidate cache (e.g., after successful creation)
      // We'll check localStorage for a flag or just refresh on mount
      const shouldRefresh = sessionStorage.getItem('shouldRefreshPatrolSchedules');
      if (shouldRefresh === 'true') {
        sessionStorage.removeItem('shouldRefreshPatrolSchedules');
        fetchPatrolSchedules(true); // Force refresh
      }
    }
  }, [showCreatePatrol, showDetails, fetchPatrolSchedules]);

  // Handle search functionality
  const handleSearchChange = e => {
    const value = e.target.value;
    setSearchTerm(value);

    // Filter schedules based on search term
    if (value.length > 0) {
      const filtered = patrolSchedules.filter(patrol => {
        const searchLower = value.toLowerCase();
        return (
          (patrol.patrol_id || patrol.id)?.toString().toLowerCase().includes(searchLower) ||
          patrol.patrol_name?.toLowerCase().includes(searchLower) ||
          patrol.startQrName?.toLowerCase().includes(searchLower) ||
          patrol.endQrName?.toLowerCase().includes(searchLower) ||
          patrol.startQrAddress?.toLowerCase().includes(searchLower) ||
          patrol.endQrAddress?.toLowerCase().includes(searchLower) ||
          patrol.supervisorName?.toLowerCase().includes(searchLower)
        );
      });
      setFilteredSchedules(filtered);
      
      // Generate suggestions
      const suggestions = filtered.slice(0, 5).map(patrol => 
        patrol.patrol_name || patrol.startQrName || 'Unknown QR'
      );
      setSearchSuggestions(suggestions);
    } else {
      setFilteredSchedules(patrolSchedules);
      setSearchSuggestions([]);
    }
  };

  const handleSearchSubmit = value => {
    console.log('Search submitted:', value);
    // Search is already handled in handleSearchChange
  };

  const handleSuggestionClick = suggestion => {
    setSearchTerm(suggestion);
    setSearchSuggestions([]);
    // Filter is already applied in handleSearchChange
  };

  const handlePatrolClick = (patrol) => {
    setSelectedPatrol(patrol);
    // Update URL to show patrol details
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('view', 'patrol-details');
    newSearchParams.set('patrolId', (patrol.patrol_id || patrol.id)?.toString());
    setSearchParams(newSearchParams);
  };

  const handleBackFromDetails = () => {
    setSelectedPatrol(null);
    // Remove query params to go back to list view
    setSearchParams({});
  };

  const handleCreatePatrol = () => {
    // Navigate to create patrol schedule page
    setSearchParams({ view: 'create-patrol' });
  };

  // Handle loading more patrols (pagination)
  const handleLoadMore = () => {
    if (pagination.has_more && !isLoading) {
      fetchPatrolSchedules(false, true);
    }
  };

  // Handle filter changes
  const handleFilterChange = async (filterType, value) => {
    const newFilters = {
      ...filters,
      [filterType]: value || null,
    };
    setFilters(newFilters);
    
    // Reset pagination when filters change
    setPagination({
      has_more: false,
      last_key: null,
      count: 0,
    });

    // Fetch with new filters
    setIsLoading(true);
    setError(null);
    try {
      const apiOptions = {
        limit: 100,
      };

      if (newFilters.status) {
        apiOptions.status = newFilters.status;
      }
      if (newFilters.date) {
        apiOptions.date = newFilters.date;
      }
      if (newFilters.user_id) {
        apiOptions.user_id = newFilters.user_id;
      }

      const response = await patrollingApi.getAll(apiOptions);
      const schedules = response.patrols || [];

      setPagination({
        has_more: response.has_more || false,
        last_key: response.last_key || null,
        count: response.count || schedules.length,
      });

      // Ensure QR cache is loaded and get the current cache
      const currentQrCache = await fetchQRCodes();

      // Enrich schedules with QR details
      const enrichedSchedules = await Promise.all(
        schedules.map(async (schedule) => {
          try {
            // Get start QR details
            const startQrId = schedule.start_qr_id;
            const startQr = startQrId ? currentQrCache.get(startQrId) : null;
            const startQrName = startQr?.name || startQrId || 'Unknown';
            const startQrAddress = startQr?.address || 'No address';
            
            // Get end QR details
            const endQrId = schedule.end_qr_id;
            const endQr = endQrId ? currentQrCache.get(endQrId) : null;
            const endQrName = endQr?.name || endQrId || 'Unknown';
            const endQrAddress = endQr?.address || 'No address';

            let supervisorName = 'Unknown';
            const supervisorId = schedule.activity_done_by?.split('/')?.[1] || 
                                schedule.supervisor_id ||
                                schedule.patrol_details?.[0]?.user_ids?.[0];
            
            if (supervisorId) {
              try {
                const supervisorDetails = await leaveApi.getUserInfo(supervisorId);
                supervisorName = supervisorDetails?.user_name || supervisorDetails?.name || 'Unknown';
              } catch (error) {
                console.error('Error fetching supervisor details:', error);
              }
            }

            return {
              ...schedule,
              startQrName,
              startQrAddress,
              startQrId,
              endQrName,
              endQrAddress,
              endQrId,
              supervisorName,
              id: schedule.patrol_id || schedule.id,
            };
          } catch (error) {
            console.error('Error enriching schedule:', error);
            return {
              ...schedule,
              startQrName: schedule.start_qr_id || 'Unknown',
              startQrAddress: 'No address',
              startQrId: schedule.start_qr_id,
              endQrName: schedule.end_qr_id || 'Unknown',
              endQrAddress: 'No address',
              endQrId: schedule.end_qr_id,
              supervisorName: 'Unknown',
              id: schedule.patrol_id || schedule.id,
            };
          }
        })
      );

      patrolScheduleCache.set(enrichedSchedules);
      setPatrolSchedules(enrichedSchedules);
      setFilteredSchedules(enrichedSchedules);
    } catch (error) {
      console.error('Error fetching patrol schedules:', error);
      setError(error.message || 'Failed to fetch patrol schedules. Please try again.');
      setPatrolSchedules([]);
      setFilteredSchedules([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'N/A';
    }
  };

  if (showDetails && selectedPatrol) {
    return (
      <PatroleDetails 
        patrol={selectedPatrol} 
        onBack={handleBackFromDetails}
      />
    );
  }

  if (showCreatePatrol) {
    return <CreatePatrollingSchedule />;
  }

  if (showAssignConvoy) {
    return <PatrollingAssignConvo />;
  }

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header Section */}
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h2 className={typography.h2} style={{ color: '#000000' }}>
            Schedule a Patrolling
          </h2>
          <p className={typography.p} style={{ color: '#666666', marginTop: '0.5rem' }}>
            View and manage scheduled patrols
          </p>
        </div>
        {/* Create Patrol Button */}
        <div className="mt-auto pt-4">
          <IconTextButton
            icon={<MapPin className="w-6 h-6 text-white" />}
            text="Create Patrolling Schedule"
            onClick={handleCreatePatrol}
            className="w-full"
            size="lg"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          <p className="font-semibold">Error loading patrol schedules</p>
          <p className="text-sm">{error}</p>
          <button
            onClick={() => fetchPatrolSchedules(true)}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Search Bar Section */}
      <div className="mb-6">
        <SearchBar
          placeholder="Search by QR name, patrol name, or supervisor..."
          value={searchTerm}
          onChange={handleSearchChange}
          onSearch={handleSearchSubmit}
          suggestions={searchSuggestions}
          onSuggestionClick={handleSuggestionClick}
          className="w-full max-w-lg"
        />
      </div>

      {/* Filters Section */}
      <div className="mb-6 flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status
          </label>
          <select
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="w-full h-10 px-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="">All Statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="missed">Missed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date
          </label>
          <input
            type="date"
            value={filters.date || ''}
            onChange={(e) => handleFilterChange('date', e.target.value)}
            className="w-full h-10 px-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        {(filters.status || filters.date) && (
          <button
            onClick={() => {
              setFilters({ status: null, date: null, user_id: null });
              setPagination({ has_more: false, last_key: null, count: 0 });
            }}
            className="h-10 px-4 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Scheduled Patrols Section */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h3 className={typography.h3} style={{ color: '#000000' }}>
            Scheduled Patrols
          </h3>
          {pagination.count > 0 && (
            <p className="text-sm text-gray-600 mt-1">
              Showing {patrolSchedules.length} of {pagination.count} patrol{pagination.count !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <button
          onClick={() => fetchPatrolSchedules(true)}
          disabled={isLoading}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
          title="Refresh"
        >
          <RefreshCw className={`w-5 h-5 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Patrols List */}
      <div className="flex-1 overflow-y-auto mb-6">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">Loading patrol schedules...</div>
          </div>
        ) : filteredSchedules.length === 0 ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500 text-center">
              <p className="text-lg mb-2">No patrols found</p>
              <p className="text-sm">Try adjusting your search or create a new patrol schedule</p>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {filteredSchedules.map((patrol) => (
                <PatrolScheduleCard
                  key={patrol.patrol_id || patrol.id || `patrol-${patrol.patrol_name || 'unknown'}`}
                  patrol={patrol}
                  onClick={() => handlePatrolClick(patrol)}
                  formatTimestamp={formatTimestamp}
                />
              ))}
            </div>
            {/* Load More Button */}
            {pagination.has_more && (
              <div className="mt-6 flex justify-center">
                <button
                  onClick={handleLoadMore}
                  disabled={isLoading}
                  className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div> 
    </div>
  );
};

export default ScheduleAPatrole;

